// 📄 components/StepCCTunnel.tsx
import React, { useState } from 'react';
import { Play, RefreshCw, CheckCircle2, ShieldAlert } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepCCTunnelProps {
  ccTunnelSubaccount: string;
  ccTunnelStatus: string;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
  isInvalid: boolean;
}

export function StepCCTunnel({
  ccTunnelSubaccount,
  ccTunnelStatus,
  onChange,
  isInvalid
}: StepCCTunnelProps) {
  const [checking, setChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<'idle' | 'success' | 'error'>('idle');

  const runTunnelCheck = () => {
    setChecking(true);
    setCheckResult('idle');
    setTimeout(() => {
      setChecking(false);
      if (ccTunnelSubaccount.trim().length >= 5) {
        setCheckResult('success');
        onChange('ccTunnelStatus', 'Connected');
      } else {
        setCheckResult('error');
        onChange('ccTunnelStatus', 'Disconnected');
      }
    }, 2000);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block">SAP BTP Connectivity</span>
        <p className="font-mono text-xs text-gray-300">
          <span>SAP BTP Cockpit → Subaccount → Connectivity → Cloud Connectors</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-indigo-400">3. Estado de Conexión del Túnel SCC ↔ BTP</h3>
        <p className="text-sm text-gray-400 mt-1">
          Valida que el túnel de conexión entre tu Cloud Connector local (SCC) y la subcuenta (Subaccount) de SAP BTP
          esté activo y reportando un estado saludable.
        </p>
      </div>

      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            BTP Subaccount ID (Technical Name) <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={ccTunnelSubaccount}
            onChange={(e) => onChange('ccTunnelSubaccount', e.target.value)}
            placeholder="ej. a1b2c3d4-5678-90ab-cdef-1234567890ab"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm font-mono text-white focus:outline-none focus:border-indigo-500 ${
              isInvalid && !ccTunnelSubaccount ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Estado Detectado</label>
          <select
            value={ccTunnelStatus || 'Disconnected'}
            onChange={(e) => onChange('ccTunnelStatus', e.target.value)}
            className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
          >
            <option value="Disconnected">Disconnected (Inactivo)</option>
            <option value="Connected">Connected (Túnel Establecido)</option>
            <option value="Error">Error / Expired Certificate</option>
          </select>
        </div>
      </div>

      <div className="bg-zinc-900/40 border border-white/5 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="space-y-1 text-center sm:text-left">
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block">Verificar Disponibilidad del Túnel</span>
          <p className="text-xs text-gray-400">
            Realiza un ping técnico para validar el handshake de seguridad TLS y el estado del túnel BTP Connectivity.
          </p>
        </div>
        
        <button
          type="button"
          onClick={runTunnelCheck}
          disabled={checking}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-[#fff] font-medium text-xs px-4 py-2.5 rounded-lg shadow-md transition-all disabled:bg-gray-600 active:scale-[0.98]"
        >
          {checking ? (
            <>
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              Chequeando túnel...
            </>
          ) : (
            <>
              <Play className="w-3.5 h-3.5" />
              Validar Conexión
            </>
          )}
        </button>
      </div>

      {checkResult === 'success' && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">Túnel Activo</span>
            <span className="text-xs text-green-500/80">
              Conexión bidireccional SSL establecida correctamente con la Subaccount {ccTunnelSubaccount}. Los recursos están listos para iFlows.
            </span>
          </div>
        </div>
      )}

      {checkResult === 'error' && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
          <div>
            <span className="font-semibold block">Error de Conexión</span>
            <span className="text-xs text-red-500/80">
              No se pudo establecer el handshake con BTP. Verifica que las credenciales de la Subaccount y el Location ID sean correctos en el SCC.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
